'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { forwardRef, type PropsWithChildren, type ReactNode } from 'react'

import { InfoTooltip } from './InfoTooltip'

export interface DataListRootProps extends PropsWithChildren {
  fullWidth?: boolean
  className?: string
}

const DataListRoot = forwardRef<HTMLDivElement, DataListRootProps>(
  ({ className, children, fullWidth }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'gap-xl flex flex-row justify-between',
          'max-xl:gap-lg',
          'max-lg:flex-col max-lg:gap-0',
          fullWidth && 'w-full',
          className
        )}
      >
        {children}
      </div>
    )
  }
)
DataListRoot.displayName = 'DataListRoot'

export interface DataListColumnProps extends PropsWithChildren {
  className?: string
}

const DataListColumn = forwardRef<HTMLDivElement, DataListColumnProps>(
  ({ className, children }, ref) => {
    return (
      <div ref={ref} className={cn('flex w-full flex-col', className)}>
        {children}
      </div>
    )
  }
)
DataListColumn.displayName = 'DataListColumn'

export interface DataListLineProps extends PropsWithChildren {
  className?: string
}

const DataListLine = forwardRef<HTMLDivElement, DataListLineProps>(
  ({ className, children }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'gap-xs flex h-[44px] items-center justify-between',
          'border-b-[0.5px] border-border',
          className
        )}
      >
        {children}
      </div>
    )
  }
)
DataListLine.displayName = 'DataListLine'

export interface DataListLeftItemPropsWithLabel {
  label: string
  tooltip?: ReactNode
  children?: never
  className?: string
}

export interface DataListLeftItemPropsWithChildren {
  children: ReactNode
  label?: never
  tooltip?: never
  className?: string
}

export type DataListLeftItemProps =
  | DataListLeftItemPropsWithLabel
  | DataListLeftItemPropsWithChildren

const DataListLeftItem = forwardRef<HTMLDivElement, DataListLeftItemProps>(
  ({ label, tooltip, className, children }, ref) => {
    return (
      <div ref={ref} className={cn('gap-2xs flex items-center justify-center', className)}>
        {children ?? (
          <>
            <span className="text-secondary-foreground text-xs">{label}</span>
            {tooltip && (
              <InfoTooltip
                tooltipContent={tooltip}
                className="text-secondary-foreground hover:text-foreground size-5 transition-colors"
              />
            )}
          </>
        )}
      </div>
    )
  }
)
DataListLeftItem.displayName = 'DataListLeftItem'

export interface DataListRightItemPropsWithLabel {
  label: string
  children?: never
  className?: string
}

export interface DataListRightItemPropsWithChildren {
  children: ReactNode
  label?: never
  className?: string
}

export type DataListRightItemProps =
  | DataListRightItemPropsWithLabel
  | DataListRightItemPropsWithChildren

const DataListRightItem = forwardRef<HTMLDivElement, DataListRightItemProps>(
  ({ label, children, className }, ref) => {
    return (
      <div ref={ref} className={cn('gap-2xs flex items-center text-right', className)}>
        {children ?? <span className="text-foreground text-xs">{label}</span>}
      </div>
    )
  }
)
DataListRightItem.displayName = 'DataListRightItem'

export const DataList = {
  Root: DataListRoot,
  Column: DataListColumn,
  Line: DataListLine,
  LeftItem: DataListLeftItem,
  RightItem: DataListRightItem
}
